import { Watch, Prop, Component, Vue } from 'vue-property-decorator';
import ClIcon from '../ClIcon';
import ClIconButton from '../ClIconButton';
import { applyClasses } from '../../lib';

type Classes = {
  root: string | object;
  iconButton: string | object;
  icon: string | object;
  input: string | object;
  bar: string | object;
};

@Component({
  components: {
    ClIconButton,
    ClIcon
  }
})
export default class ClSwitch extends Vue {
  /**
   * If `true`, the component is checked.
   * If this is set to either true or false explicity,
   * the assumption is that it's checked state is controlled externally.
   */
  @Prop({
    type: Boolean,
    default: null
  })
  public checked!: boolean;

  /**
   * The classnames of the internal components.
   */
  @Prop({
    type: Object as () => Classes,
    default: () => ({})
  })
  public classes!: Classes;

  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  @Prop({
    type: String,
    validator: x => ['primary', 'secondary', 'default'].includes(x),
    default: 'secondary'
  })
  public color!: string;

  /**
   * If `true`, the component is checked by default,
   * while it's checked state is controlled internally.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public defaultChecked!: boolean;

  /**
   * If `true`, the switch will be disabled.
   */
  @Prop(Boolean)
  public disabled!: boolean;

  /**
   * If `true`, the switch will be in an error state.
   */
  @Prop(Boolean)
  public error!: boolean;

  /**
   * Value when unchecked
   */
  @Prop({
    type: [String, Number, Boolean],
    default: false
  })
  public falseValue!: string | number | boolean;

  /**
   * If `true`, `checkedState` will be controlled externally.
   */
  public isControlled: boolean = this.checked !== null;

  /**
   * The id of the `input` element.
   */
  @Prop(String)
  public id!: string;

  /**
   * Props for the input element
   */
  @Prop(Object)
  public inputProps!: object;

  /**
   * Value when checked
   */
  @Prop({
    type: [String, Number, Boolean],
    default: true
  })
  public trueValue!: string | number | boolean;

  /**
   * The value of the component.
   */
  @Prop({
    type: [String, Number, Boolean]
  })
  public value?: string | boolean | number;

  /**
   * Checked state, for use when component is not controlled externally.
   */
  public checkedState?: boolean =
    this.checked || this.defaultChecked || this.value === this.trueValue;

  @Watch('value')
  watchExternalValueChange(newValue: string | boolean | number) {
    this.checkedState = newValue == this.trueValue;
  }

  /**
   * We maintain the internal checked state if the checked prop isnt set externally
   */
  public handleChange(event: Event) {
    this.$emit('change', event);
    const { checked } = event.target as HTMLInputElement;
    if (this.isControlled) {
      this.checkedState = this.checked;
      return;
    }
    this.checkedState = checked;
  }

  public handleInput(event: Event): void {
    const { checked } = event.target as HTMLInputElement;
    this.$emit('input', checked ? this.trueValue : this.falseValue);
  }

  get rootClasses(): object {
    return applyClasses(
      {
        'cl-switch': true,
        'cl-switch--checked': this.checkedState,
        [`cl-switch--color-${this.color}`]: true
      },
      this.classes.root
    );
  }

  get iconButtonClasses(): object {
    return applyClasses(
      { 'cl-switch__icon-button': true },
      this.classes.iconButton
    );
  }

  get iconClasses(): object {
    return applyClasses({ 'cl-switch__icon': true }, this.classes.icon);
  }

  get inputClasses(): object {
    return applyClasses({ 'cl-switch__input': true }, this.classes.input);
  }

  get barClasses(): object {
    return applyClasses({ 'cl-switch__bar': true }, this.classes.bar);
  }
}
